import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, MapPin, Clock, Navigation2, LogIn, LogOut, Package, AlertOctagon,
  CheckCircle2, FileSignature, Loader2, Plus,
} from "lucide-react";
import api, { formatApiError } from "../lib/api";
import PhotoGallery from "../components/PhotoGallery";

function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" }); } catch { return ""; }
}
function fullAddr(addr) {
  if (!addr) return "";
  return [addr.line1, addr.suburb, addr.state, addr.postcode].filter(Boolean).join(", ");
}
function mapsLink(addr) {
  if (!addr) return null;
  if (addr.lat && addr.lng) return `https://www.google.com/maps/dir/?api=1&destination=${addr.lat},${addr.lng}`;
  const a = fullAddr(addr);
  return a ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(a)}` : null;
}

export default function FieldJobDetail() {
  const { blockId } = useParams();
  const nav = useNavigate();
  const [block, setBlock] = useState(null);
  const [project, setProject] = useState(null);
  const [visit, setVisit] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [variations, setVariations] = useState([]);
  const [completion, setCompletion] = useState(null);
  const [showMat, setShowMat] = useState(false);
  const [showVar, setShowVar] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: blocks }, { data: visits }, { data: mats }, { data: vars }, { data: comps }] =
        await Promise.all([
          api.get(`/api/schedule?project_id=`).catch(() => ({ data: [] })),
          api.get(`/api/field/visits?block_id=${blockId}`),
          api.get(`/api/field/materials-received?block_id=${blockId}`),
          api.get(`/api/field/variations`),
          api.get(`/api/field/completions?block_id=${blockId}`),
        ]);
      // Best-effort: fetch the single block via my-day filter (works for crew_member)
      const myDay = await api.get("/api/field/my-day").catch(() => ({ data: { blocks: [] } }));
      const b = (myDay.data.blocks || []).find((x) => x.id === blockId) ||
                (blocks || []).find((x) => x.id === blockId);
      setBlock(b || null);
      setVisit(visits.find((v) => v.status === "in_progress") || visits[0] || null);
      setMaterials(mats);
      const blockVars = vars.filter((v) => v.block_id === blockId);
      setVariations(blockVars);
      setCompletion(comps[0] || null);
      if (b?.project_id) {
        try {
          const { data: pj } = await api.get(`/api/projects/${b.project_id}`);
          setProject(pj);
        } catch {/* crew_member may not have project access — that's fine */}
      }
    } catch (e) {
      toast.error("Could not load job");
    }
  }, [blockId]);

  useEffect(() => { load(); }, [load]);

  const addr = block?.project_address || project?.address;
  const mapHref = mapsLink(addr);

  const onCheckIn = async () => {
    setBusy(true);
    try {
      let coords = {};
      if (navigator.geolocation) {
        await new Promise((resolve) =>
          navigator.geolocation.getCurrentPosition(
            (pos) => { coords = { lat: pos.coords.latitude, lng: pos.coords.longitude }; resolve(); },
            () => resolve(),
            { timeout: 4000, maximumAge: 60000 }
          )
        );
      }
      const { data: v } = await api.post("/api/field/visits/check-in", { block_id: blockId, ...coords });
      setVisit(v);
      toast.success("Checked in onsite");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const onCheckOut = async () => {
    if (!visit) return;
    setBusy(true);
    try {
      await api.post(`/api/field/visits/${visit.id}/check-out`, {});
      toast.success("Checked out");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  if (!block) {
    return (
      <div className="px-4 pt-4" data-testid="field-job-loading">
        <Link to="/" className="inline-flex items-center gap-1 text-[12px] font-bold" style={{ color: "var(--ink-muted)" }}>
          <ArrowLeft size={13} /> My Day
        </Link>
        <div className="mt-4 text-[13px]" style={{ color: "var(--ink-muted)" }}>Loading job…</div>
      </div>
    );
  }

  return (
    <div data-testid="field-job-detail" className="px-4 pt-4 pb-6 max-w-[640px] mx-auto">
      <Link to="/" className="inline-flex items-center gap-1 text-[12px] font-bold" style={{ color: "var(--ink-muted)" }} data-testid="back-to-my-day">
        <ArrowLeft size={13} /> My Day
      </Link>

      {/* Site card */}
      <div
        className="mt-3 surface-1 p-4"
        data-testid="site-card"
        style={{ borderLeft: "4px solid var(--accent)" }}
      >
        <div className="flex items-center gap-1.5 text-[11px]">
          <span className="font-mono font-extrabold" style={{ color: "var(--brand)" }} data-testid="site-project-code">
            {block.project_code}
          </span>
          <span className="opacity-30">·</span>
          <span className="text-[11px] font-bold uppercase tracking-[0.10em]" style={{ color: "var(--ink-muted)" }}>
            <Clock size={11} className="inline -mt-[2px] mr-1" />
            {fmtTime(block.start)} → {fmtTime(block.end)}
          </span>
        </div>
        <h1 className="text-[18px] font-extrabold leading-tight mt-1.5" style={{ color: "var(--ink)" }}>
          {block.project_name}
        </h1>
        {addr && (
          <div className="text-[12px] mt-1" style={{ color: "var(--ink-soft)" }}>
            <MapPin size={12} className="inline -mt-[2px] mr-1" />{fullAddr(addr) || "No address"}
          </div>
        )}

        {/* Maps stub container — directions ready, satellite deferred */}
        <div
          className="mt-3 aspect-[16/9] flex items-center justify-center text-center px-4"
          data-testid="maps-stub"
          style={{
            background: "var(--bg-elev-2)",
            border: "1px dashed var(--line-strong)",
            borderRadius: 4,
          }}
        >
          <div>
            <MapPin size={18} style={{ color: "var(--ink-faint)", margin: "0 auto" }} />
            <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] mt-1.5" style={{ color: "var(--ink-muted)" }}>
              Satellite view
            </div>
            <div className="text-[10.5px] mt-1" style={{ color: "var(--ink-faint)" }}>
              Activates when Google Maps key is configured
            </div>
          </div>
        </div>

        {mapHref && (
          <a
            href={mapHref}
            target="_blank"
            rel="noreferrer"
            data-testid="site-directions-btn"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-3 rounded-md text-[12.5px] font-extrabold uppercase tracking-[0.10em]"
            style={{ background: "var(--bg-nav)", color: "#fff", minHeight: 48 }}
          >
            <Navigation2 size={14} /> Open Directions
          </a>
        )}
      </div>

      {/* Workflow ladder — guides crew through priority order */}
      <div className="mt-4 space-y-2.5">
        {/* 1. Arrival */}
        <StepCard
          stepNum={1}
          title={visit ? (visit.status === "in_progress" ? "Onsite — arrival captured" : "Visit recorded") : "Arrive onsite"}
          tone={visit ? "good" : "active"}
          testId="step-arrival"
          done={!!visit}
          action={
            !visit ? (
              <button
                onClick={onCheckIn}
                disabled={busy}
                data-testid="check-in-btn"
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-3 rounded-md text-[12.5px] font-extrabold uppercase tracking-[0.10em] disabled:opacity-60"
                style={{ background: "var(--accent)", color: "#fff", minHeight: 48 }}
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <LogIn size={14} />} Check in onsite
              </button>
            ) : visit.status === "in_progress" ? (
              <button
                onClick={onCheckOut}
                disabled={busy}
                data-testid="check-out-btn"
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-3 rounded-md text-[12.5px] font-extrabold uppercase tracking-[0.10em] disabled:opacity-60"
                style={{ background: "var(--bg-nav)", color: "#fff", minHeight: 44 }}
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <LogOut size={14} />} Check out
              </button>
            ) : (
              <div className="text-[11px]" style={{ color: "var(--ink-muted)" }}>
                Checked in {fmtTime(visit.started_at)} · out {fmtTime(visit.ended_at)}
              </div>
            )
          }
        />

        {/* 2. Materials */}
        <StepCard
          stepNum={2}
          title={materials.length > 0 ? `Materials received (${materials.length})` : "Materials received"}
          tone={materials.length > 0 ? "good" : "neutral"}
          testId="step-materials"
          done={materials.length > 0}
          action={
            <>
              <button
                onClick={() => setShowMat(true)}
                data-testid="open-materials-btn"
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-md text-[12px] font-bold uppercase tracking-[0.10em]"
                style={{ background: "var(--bg-elev-2)", border: "1px solid var(--line-strong)", color: "var(--ink)", minHeight: 44 }}
              >
                <Package size={13} /> Log delivery
              </button>
              {materials.length > 0 && (
                <ul className="mt-2 space-y-1 text-[11.5px]" data-testid="materials-list">
                  {materials.map((m) => (
                    <li key={m.id} className="px-2 py-1 rounded-md" style={{ background: "var(--bg-elev-2)" }}>
                      <span className="font-bold" style={{ color: "var(--ink)" }}>{m.supplier || "site"}</span>
                      <span className="opacity-30 mx-1.5">·</span>
                      <span style={{ color: "var(--ink-soft)" }}>{(m.items || []).join(", ") || "delivery"}</span>
                      <span className="ml-1.5 chip chip-good" data-testid={`mat-condition-${m.id}`}>{m.condition}</span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          }
        />

        {/* 3. Variations */}
        <StepCard
          stepNum={3}
          title={variations.length > 0 ? `Variations (${variations.length})` : "Variations / issues"}
          tone={variations.some((v) => v.status === "pending_approval") ? "warn" : variations.length > 0 ? "good" : "neutral"}
          testId="step-variations"
          done={false}
          action={
            <>
              <button
                onClick={() => setShowVar(true)}
                data-testid="open-variation-btn"
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-md text-[12px] font-bold uppercase tracking-[0.10em]"
                style={{ background: "var(--bg-elev-2)", border: "1px solid var(--line-strong)", color: "var(--ink)", minHeight: 44 }}
              >
                <Plus size={13} /> Capture variation
              </button>
              {variations.length > 0 && (
                <ul className="mt-2 space-y-1 text-[11.5px]" data-testid="variations-list">
                  {variations.map((v) => (
                    <li key={v.id} className="px-2 py-2 rounded-md" style={{ background: "var(--bg-elev-2)" }}>
                      <div className="flex items-center gap-2">
                        <AlertOctagon size={11} style={{ color: "var(--warn)" }} />
                        <span className="font-bold flex-1 truncate" style={{ color: "var(--ink)" }}>{v.title}</span>
                        <span className={"chip " +
                          (v.status === "approved" ? "chip-good"
                           : v.status === "rejected" ? "chip-bad" : "chip-warn")}
                              data-testid={`variation-status-${v.id}`}>
                          {v.status.replace("_", " ")}
                        </span>
                      </div>
                      <div className="text-[10.5px] mt-0.5 font-mono" style={{ color: "var(--ink-muted)" }}>
                        ${(v.estimated_amount || 0).toLocaleString("en-AU", { minimumFractionDigits: 2 })}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          }
        />

        {/* 4. Sign-off */}
        <StepCard
          stepNum={4}
          title={completion ? "Signed off" : "Customer sign-off"}
          tone={completion ? "good" : "active"}
          testId="step-signoff"
          done={!!completion}
          action={
            completion ? (
              <div className="space-y-2">
                <div className="text-[11.5px]" style={{ color: "var(--ink-soft)" }} data-testid="signoff-summary">
                  <span className="font-bold" style={{ color: "var(--ink)" }}>{completion.customer_name_typed}</span>
                  <span className="opacity-30 mx-1.5">·</span>
                  {fmtTime(completion.signed_at)}
                  {completion.signature_upload_id && (
                    <span className="ml-2 chip chip-good">SIGNATURE</span>
                  )}
                </div>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/field/completions/${completion.id}/certificate.pdf`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="download-certificate-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[11px] font-extrabold uppercase tracking-[0.10em]"
                  style={{ background: "var(--bg-nav)", color: "#fff", minHeight: 36 }}
                >
                  Completion certificate (PDF)
                </a>
              </div>
            ) : (
              <button
                onClick={() => nav(`/jobs/${blockId}/sign-off`)}
                data-testid="open-signoff-btn"
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-3 rounded-md text-[12.5px] font-extrabold uppercase tracking-[0.10em]"
                style={{ background: "var(--brand)", color: "#fff", minHeight: 48 }}
              >
                <FileSignature size={14} /> Capture sign-off
              </button>
            )
          }
        />
      </div>

      {/* Photo gallery — operational visual memory */}
      <div className="mt-5">
        <PhotoGallery
          projectId={block.project_id}
          uploadLinkedKind="block"
          uploadLinkedId={blockId}
          defaultCategory="during"
        />
      </div>

      {showMat && (
        <MaterialsModal
          blockId={blockId}
          projectId={block.project_id}
          onClose={() => setShowMat(false)}
          onSaved={() => { setShowMat(false); load(); }}
        />
      )}
      {showVar && (
        <VariationModal
          blockId={blockId}
          projectId={block.project_id}
          onClose={() => setShowVar(false)}
          onSaved={() => { setShowVar(false); load(); }}
        />
      )}
    </div>
  );
}

function StepCard({ stepNum, title, tone = "neutral", done, action, testId }) {
  const accent =
    tone === "good" ? "var(--good)"
    : tone === "warn" ? "var(--warn)"
    : tone === "active" ? "var(--accent)"
    : "var(--line-strong)";
  return (
    <div
      data-testid={testId}
      className="surface-1 p-3"
      style={{ borderLeft: `4px solid ${accent}` }}
    >
      <div className="flex items-center gap-2 mb-2">
        <div
          className="w-6 h-6 rounded-md flex items-center justify-center text-[11px] font-extrabold font-mono"
          style={{ background: done ? "var(--good)" : "var(--bg-elev-3)", color: done ? "#fff" : "var(--ink)" }}
        >
          {done ? <CheckCircle2 size={13} /> : stepNum}
        </div>
        <div className="text-[12.5px] font-bold" style={{ color: "var(--ink)" }}>{title}</div>
      </div>
      <div>{action}</div>
    </div>
  );
}

function MaterialsModal({ blockId, projectId, onClose, onSaved }) {
  const [supplier, setSupplier] = useState("");
  const [items, setItems] = useState("");
  const [condition, setCondition] = useState("ok");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/api/field/materials-received", {
        block_id: blockId,
        project_id: projectId,
        supplier: supplier || null,
        items: items.split("\n").map((s) => s.trim()).filter(Boolean),
        condition,
        notes: notes || null,
      });
      toast.success("Materials logged");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <ModalShell title="Materials received" onClose={onClose} testId="materials-modal">
      <Field label="Supplier">
        <input data-testid="mat-supplier" value={supplier} onChange={(e) => setSupplier(e.target.value)}
               placeholder="QSM / Lysaght / other" className="w-full" />
      </Field>
      <Field label="Items (one per line)">
        <textarea data-testid="mat-items" rows={4} value={items} onChange={(e) => setItems(e.target.value)}
                  placeholder="120 sqm Corro Dek 0.42&#10;30 LM Ridge Capping" className="w-full font-mono text-[12px]" />
      </Field>
      <Field label="Condition">
        <div className="grid grid-cols-3 gap-2" data-testid="mat-condition-group">
          {["ok", "damaged", "short"].map((k) => (
            <button
              key={k}
              type="button"
              data-testid={`mat-cond-${k}`}
              onClick={() => setCondition(k)}
              className="px-2 py-2 text-[11px] font-bold uppercase tracking-[0.10em] rounded-md"
              style={{
                background: condition === k ? "var(--brand)" : "var(--bg-elev-2)",
                color: condition === k ? "#fff" : "var(--ink)",
                border: "1px solid var(--line-strong)",
                minHeight: 44,
              }}
            >{k}</button>
          ))}
        </div>
      </Field>
      <Field label="Notes (optional)">
        <textarea data-testid="mat-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full" />
      </Field>
      <ModalActions onCancel={onClose} onSave={save} busy={busy} saveTestId="save-materials-btn" saveLabel="Log delivery" />
    </ModalShell>
  );
}

function VariationModal({ blockId, projectId, onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [kind, setKind] = useState("scope_change");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!title.trim() || !description.trim()) {
      toast.error("Please add a title and a short description.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/api/field/variations", {
        project_id: projectId,
        block_id: blockId,
        title: title.trim(),
        description: description.trim(),
        kind,
        estimated_amount: parseFloat(amount) || 0,
        photo_ids: [],
      });
      toast.success("Variation saved · waiting on approval");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <ModalShell title="Capture variation" onClose={onClose} testId="variation-modal">
      <Field label="Title">
        <input data-testid="var-title" value={title} onChange={(e) => setTitle(e.target.value)}
               placeholder="Additional ridge capping required" className="w-full" />
      </Field>
      <Field label="What & where">
        <textarea data-testid="var-description" rows={3} value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="South elevation found 8 LM extra ridge cap required to seal join" className="w-full" />
      </Field>
      <Field label="Type">
        <select data-testid="var-kind" value={kind} onChange={(e) => setKind(e.target.value)} className="w-full">
          <option value="scope_change">Scope change</option>
          <option value="extra_work">Extra work</option>
          <option value="materials_extra">Extra materials</option>
          <option value="access_issue">Access issue</option>
          <option value="defect">Defect</option>
          <option value="other">Other</option>
        </select>
      </Field>
      <Field label="Estimated $ (inc GST)">
        <input data-testid="var-amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)}
               placeholder="0.00" className="w-full font-mono" />
      </Field>
      <ModalActions onCancel={onClose} onSave={save} busy={busy} saveTestId="save-variation-btn" saveLabel="Submit variation" />
    </ModalShell>
  );
}

function ModalShell({ title, onClose, children, testId }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: "rgba(14,14,24,0.55)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-[460px] surface-2 p-5 fade-up"
        style={{
          background: "var(--bg-elev-1)",
          borderTopLeftRadius: 14,
          borderTopRightRadius: 14,
          borderRadius: 14,
          maxHeight: "90vh",
          overflowY: "auto",
        }}
        data-testid={testId}
      >
        <div className="text-[15px] font-extrabold mb-3" style={{ color: "var(--ink)" }}>{title}</div>
        <div className="space-y-3">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10.5px] font-extrabold uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ink-muted)" }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function ModalActions({ onCancel, onSave, busy, saveTestId, saveLabel }) {
  return (
    <div className="grid grid-cols-2 gap-2 pt-2">
      <button
        type="button"
        onClick={onCancel}
        className="px-3 py-3 rounded-md text-[12px] font-bold uppercase tracking-[0.10em]"
        style={{ background: "var(--bg-elev-2)", color: "var(--ink-muted)", border: "1px solid var(--line-strong)", minHeight: 48 }}
        data-testid="modal-cancel-btn"
      >Cancel</button>
      <button
        type="button"
        onClick={onSave}
        disabled={busy}
        data-testid={saveTestId}
        className="px-3 py-3 rounded-md text-[12px] font-extrabold uppercase tracking-[0.10em] disabled:opacity-60 inline-flex items-center justify-center gap-2"
        style={{ background: "var(--brand)", color: "#fff", minHeight: 48 }}
      >
        {busy && <Loader2 size={13} className="animate-spin" />}
        {saveLabel}
      </button>
    </div>
  );
}
